import { Building, Lease, Team, Unit, UnitLease, UserTeam, getTeamChildren } from '@rentguru/commons-utils';
import isNil from 'lodash/isNil';

import { ContextLoaderStore } from './ContextLoader';

const isIdIncludedInList = (id: string, list: { id: string }[]) => list.some((element) => element.id === id);

export const filterContextLoaderBundleByTeam = (contextLoaderBundle: ContextLoaderStore, userId: string) => {
  const {
    Building: { data: buildings }, // Already filtered by user rights
    BuildingOwner: { data: buildingOwners },
    Lease: { data: leases },
    LeaseContact: { data: leaseContacts },
    Notification: { data: notifications },
    Technic: { data: technics },
    Team: { data: teams },
    Ticket: { data: tickets },
    Unit: { data: units },
    UnitLease: { data: unitLeases },
    UnitOwner: { data: unitOwners },
    UserTeam: { data: userTeams },
  } = contextLoaderBundle;

  const isUserInRootTeam = userTeams.some(
    (teamOfUser) => teamOfUser.team && teamOfUser.userId === userId && isNil(teamOfUser.team.parentId)
  );
  if (isUserInRootTeam) {
    return contextLoaderBundle;
  }
  const {
    buildings: teamBuildings,
    units: teamUnits,
    leases: teamLeases,
    unitLeases: teamUnitLeases,
    teams: teamsFiltered,
  } = getFilteredTeamsBuildingsUnitsAndLeasesForUserId(userId, userTeams, teams, buildings, units, unitLeases, leases);

  // Now that we have Leases + building + units we can filter ou technics
  const teamTechnics = technics.filter((technic) => {
    if (technic.buildingId) {
      return isIdIncludedInList(technic.buildingId, teamBuildings);
    }
    if (technic.unitId) {
      return isIdIncludedInList(technic.unitId, teamUnits);
    }
    if (technic.leaseId) {
      return isIdIncludedInList(technic.leaseId, teamLeases);
    }
    return false;
  });
  // We can now filter the notifications, communications and tickets
  const teamNotifications = notifications.filter((notification) => {
    if (notification.buildingId) {
      return isIdIncludedInList(notification.buildingId, teamBuildings);
    }
    if (notification.unitId) {
      return isIdIncludedInList(notification.unitId, teamUnits);
    }
    if (notification.leaseId) {
      return isIdIncludedInList(notification.leaseId, teamLeases);
    }
    if (notification.technicId) {
      return isIdIncludedInList(notification.technicId, teamTechnics);
    }
    return false;
  });

  const teamTickets = tickets.filter((ticket) => {
    let ticketToCheck = ticket;
    if (ticket.parentId) {
      // If the ticket is a child, we need to check the parent
      ticketToCheck = tickets.find((t) => t.id === ticket.parentId) || ticket;
    }
    if (ticketToCheck.buildingId) {
      return isIdIncludedInList(ticketToCheck.buildingId, teamBuildings);
    }
    if (ticketToCheck.unitId) {
      return isIdIncludedInList(ticketToCheck.unitId, teamUnits);
    }
    return isNil(ticketToCheck.unitId) && isNil(ticketToCheck.buildingId);
  });

  /* And finally remove the links of the contacts so that they appear but without the info
    about other entities */
  const teamBuildingOwners = buildingOwners.filter(
    (buildingOwner) => buildingOwner.buildingId && isIdIncludedInList(buildingOwner.buildingId, teamBuildings)
  );
  const teamUnitOwners = unitOwners.filter(
    (unitOwner) => unitOwner.unitId && isIdIncludedInList(unitOwner.unitId, teamUnits)
  );
  const teamLeaseContact = leaseContacts.filter(
    (leaseContact) => leaseContact.leaseId && isIdIncludedInList(leaseContact.leaseId, teamLeases)
  );
  return {
    ...contextLoaderBundle,
    Building: { ...contextLoaderBundle.Building, data: teamBuildings },
    BuildingOwner: { ...contextLoaderBundle.BuildingOwner, data: teamBuildingOwners },
    Lease: { ...contextLoaderBundle.Lease, data: teamLeases },
    LeaseContact: { ...contextLoaderBundle.LeaseContact, data: teamLeaseContact },
    Notification: { ...contextLoaderBundle.Notification, data: teamNotifications },
    Technic: { ...contextLoaderBundle.Technic, data: teamTechnics },
    Team: { ...contextLoaderBundle.Team, data: teamsFiltered },
    Ticket: { ...contextLoaderBundle.Ticket, data: teamTickets },
    Unit: { ...contextLoaderBundle.Unit, data: teamUnits },
    UnitLease: { ...contextLoaderBundle.UnitLease, data: teamUnitLeases },
    UnitOwner: { ...contextLoaderBundle.UnitOwner, data: teamUnitOwners },
  };
};

export const getFilteredTeamsBuildingsUnitsAndLeasesForUserId = (
  userId: string,
  teamsOfUser: UserTeam[],
  teams: Team[],
  buildings: Building[],
  units: Unit[],
  unitLeases: UnitLease[],
  leases: Lease[]
) => {
  const teamIdsOfUser = teamsOfUser.reduce((acc: string[], userTeam: UserTeam) => {
    if (userId && userTeam.userId === userId) {
      acc.push(userTeam.teamId);
      getTeamChildren(userTeam.teamId, teams, acc);
    }
    return acc;
  }, []);
  const teamsFiltered = teams.filter((team) => teamIdsOfUser.includes(team.id));
  const teamBuildings = buildings.filter(
    (building) => building.teams && teamIdsOfUser.some((teamId) => building.teams!.includes(teamId))
  );
  // keep only the units of the building of the teams
  const teamUnits = units.filter(
    (unit) => !isNil(unit.buildingId) && isIdIncludedInList(unit.buildingId, teamBuildings)
  );
  // Keep only the leases and lease inventories of those units
  const teamUnitLeases = unitLeases.filter(
    (unitLease) => unitLease.unitId && isIdIncludedInList(unitLease.unitId, teamUnits)
  );
  const teamLeases = teamUnitLeases.reduce((acc: Lease[], unitLease: UnitLease) => {
    if (unitLease.leaseId && !isIdIncludedInList(unitLease.leaseId, acc)) {
      const completeLease = leases.find((lease) => lease.id === unitLease.leaseId);
      if (completeLease) acc.push(completeLease);
    }
    return acc;
  }, []);
  return {
    teams: teamsFiltered,
    buildings: teamBuildings,
    units: teamUnits,
    leases: teamLeases,
    unitLeases: teamUnitLeases,
  };
};

export const canUserAccessEntity = (
  isOwner: boolean,
  readers: string[] | null | undefined,
  userId: string | null,
  teamIds: string[] | null,
  teams: string[] | null | undefined
): boolean => {
  const ownerCanRead = isOwner && !isNil(userId) && !isNil(readers) && readers.includes(userId);
  const userInTeam = !isNil(teamIds) && teamIds.some((teamId) => !isNil(teams) && teams.includes(teamId));
  return ownerCanRead || userInTeam;
};
